<template>
  <div id="claim-type-form" class="claim-type-form px-8" v-if="!createSuccess">
    <div class="claim-type-form-header-area flex flex-col gap-8">
      <div class="claim-type-form-header-area-status">
        <claim-type-status
          :status="claimTypeStatus"
          :isDisableDropdown="isClaimTypeStatusDisabled"
          :handleClickDropdownItem="changeClaimTypeStatus"
          :statusLabels="claimTypeStatusLabels"
        ></claim-type-status>
      </div>
      <div class="claim-type-form-step-area-title">ステップ</div>
    </div>

    <div
      class="claim-type-form-step-area"
      :class="{ 'pointer-events-none': !isEditable }"
    >
      <div class="claim-type-form-step-area-step-wrapper flex flex-col">
        <ClaimTypeFormStep
          v-for="claimTypeStep of claimTypeSteps"
          :ref="`claimTypeSteps-${claimTypeStep.id}`"
          :initialClaimTypeStep="claimTypeStep"
          :key="claimTypeStep.id"
          :claimItems="claimItems"
          :checkHasAddedClaimItem="checkHasAddedClaimItem"
        ></ClaimTypeFormStep>
      </div>
      <div
        class="free-text-template-area flex flex-col row-gap-8 col-gap-8 pb-16"
      >
        <div class="free-item-template-title-wrapper mt-10 flex col-gap-8">
          <span class="free-item-template-title font-bold flex-shrink-0"
            >フリー項目</span
          >
          <span class="free-annotation-text">
            ※上の項目以外で必要な情報や添付ファイルがあれば、ここに入力してください。
          </span>
        </div>
        <div class="free-text-area flex flex-col gap-3">
          <div class="free-text-sub-title">フリー記入</div>
          <div class="free-text-btn-area flex gap-x-8">
            <vs-textarea
              class="free-text-template mb-8 w-full"
              disabled
            ></vs-textarea>
            <div class="btn-area flex gap-3">
              <common-button
                class="save-btn-template"
                title="保存"
                type="filled"
                :click="() => {}"
                :btnStyle="{
                  paddingLeft: 0,
                  paddingRight: 0,
                  width: '70px',
                  height: '40px',
                }"
                disabled
              ></common-button>
              <common-button
                class="cancel-btn-template"
                type="border"
                color="#BDBDBD"
                :click="() => {}"
                :btnStyle="{
                  paddingLeft: 0,
                  paddingRight: 0,
                  width: '70px',
                  height: '40px',
                  fontSize: '12px',
                  pointerEvents: 'none',
                }"
                text-color="#757575"
                title="キャンセル"
              ></common-button>
            </div>
          </div>
        </div>
        <div class="other-attachments flex flex-col gap-3">
          <div class="free-text-sub-title">その他の添付ファイル</div>
          <CommonDrag isDisabled></CommonDrag>
        </div>
      </div>
    </div>
    <div class="claim-type-form-submit flex justify-center items-center">
      <common-button
        title="キャンセル"
        type="border"
        :color="AISEA_COLOR.GRAY"
        class="cancel-btn mr-4"
        :click="handleClickCancel"
      ></common-button>
      <common-button
        title="登録"
        class="create-btn"
        :click="handleClickCreate"
        :disabled="!isValid"
      ></common-button>
    </div>
    <template>
      <common-modal
        :title="'損種（必要情報）テンプレート登録'"
        ref="ConfirmModal"
        :modalClass="'claim-type-create-confirm'"
        :contentsClass="
          'claim-type-create-confirm-contents flex flex-col gap-8'
        "
      >
        <template v-slot:contents>
          <div class="claim-type-create-confirm-text px-4 mt-6">
            <div class="flex flex-wrap row-gap-6 col-gap-6">
              {{ claimStatusConfirmMessage }}
            </div>
          </div>
          <div class="flex justify-center">
            <div class="button-area flex justify-center">
              <common-button
                class="cancel-btn mr-4"
                type="border"
                color="#BDBDBD"
                :click="cancelConfirm"
                :btnStyle="{
                  width: '200px',
                  height: '42px',
                }"
                text-color="#757575"
                title="キャンセル"
              ></common-button>
              <common-button
                class="accept-btn"
                title="OK"
                :click="acceptConfirm"
                :btnStyle="{
                  width: '200px',
                  height: '42px',
                }"
              ></common-button>
            </div>
          </div>
        </template>
      </common-modal>
    </template>
  </div>
  <div v-else class="claim-type-form px-8">
    <div class="claim-type-form-success-area flex flex-col gap-3 pt-12 pb-12">
      <div class="claim-type-form-step-area-title">作成完了</div>
      <div class="claim-type-form-success-message">
        テンプレートの登録を完了しました。
      </div>
      <div class="to-claim-type-list-btn-wrapper flex justify-center mt-5">
        <common-button
          title="損種テンプレート一覧"
          class="to-claim-type-list-btn mr-4"
          :click="handleClickCancel"
        ></common-button>
      </div>
    </div>
  </div>
</template>

<script>
import claimTemplateService from "@/services/claimTemplateService";
const ClaimType = claimTemplateService.getClaimTypeClass();
import ClaimTypeStatus from "./ClaimTypeStatus.vue";
import CommonButton from "../components/molecules/CommonButton.vue";
import CommonModal from "../components/organisms/CommonModal.vue";
import ClaimTypeFormStep from "./ClaimTypeFormStep.vue";
import isObject from "lodash/isObject";
import cloneDeep from "lodash/cloneDeep";
import CommonDrag from "../components/molecules/CommonDrag.vue";
import { ColorMixin } from "@/mixins";
import {
  HULL_INSURANCE_CLAIM_TYPE_STATUS_LABEL,
  HULL_INSURANCE_CLAIM_TYPE_STATUSES,
  CLAIM_TYPE_STATUS_CONFIRM_MESSAGES,
} from "@/config/const";
const {
  UNREGISTERED,
  CREATING,
  TO_BE_CONFIRMED,
  REGISTERED,
} = HULL_INSURANCE_CLAIM_TYPE_STATUSES;
import Vue from "vue";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
const claimTypeRepository = AiseaApiFactory.get(
  "hullInsuranceEnterpriseClaimType"
);
const VERSION_CONFLICTED_MESSAGE =
  "他の人が保存したため反映できませんでした。ページをリロードしていただき、再度編集してください。";
const CLAIM_TEMPLATE_UPDATE_FAIL_MESSAGE =
  "損種(必要情報)テンプレート登録に失敗しました";

export default {
  name: "O.ClaimTypeForm",
  mixins: [ColorMixin],
  components: {
    ClaimTypeFormStep,
    CommonButton,
    CommonDrag,
    ClaimTypeStatus,
    CommonModal,
  },
  props: {
    initialClaimType: {
      type: ClaimType,
      required: true,
    },
    claimItems: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      claimType: null,
      createSuccess: false,
      hasAddedClaimItem: false,
      isActiveConfirm: false,
    };
  },
  watch: {
    initialClaimType: {
      handler() {
        this.claimType = isObject(this.initialClaimType)
          ? cloneDeep(this.initialClaimType)
          : new ClaimType();
      },
      immediate: true,
    },
  },
  computed: {
    hasClaimType() {
      return isObject(this.claimType);
    },
    claimTypeSteps() {
      if (!this.hasClaimType) return [];
      if (!Array.isArray(this.claimType.claimTypeSteps)) return [];
      return this.claimType.claimTypeSteps;
    },
    isValid() {
      return (
        [CREATING, TO_BE_CONFIRMED, REGISTERED].includes(
          this.claimTypeStatus
        ) || this.hasAddedClaimItem
      );
    },
    claimTypeStatus() {
      if (!this.claimType) return UNREGISTERED;
      return this.claimType.status || UNREGISTERED;
    },
    isClaimTypeStatusDisabled() {
      return this.claimTypeStatus === 1;
    },
    isEditable() {
      return [UNREGISTERED, CREATING, TO_BE_CONFIRMED].includes(
        this.claimTypeStatus
      );
    },
    claimTypeStatusLabels() {
      const {
        [CREATING]: creatingLabel,
        [TO_BE_CONFIRMED]: toBeConfirmedLabel,
        [REGISTERED]: registeredLabel,
      } = HULL_INSURANCE_CLAIM_TYPE_STATUS_LABEL;
      return {
        [CREATING]: creatingLabel,
        [TO_BE_CONFIRMED]: toBeConfirmedLabel,
        [REGISTERED]: registeredLabel,
      };
    },
    initialClaimTypeStatus() {
      if (!this.initialClaimType) return UNREGISTERED;
      if (
        !HULL_INSURANCE_CLAIM_TYPE_STATUS_LABEL[this.initialClaimType.status]
      ) {
        return UNREGISTERED;
      }
      return this.initialClaimType.status;
    },
    claimStatusConfirmMessage() {
      const confirmMessage =
        CLAIM_TYPE_STATUS_CONFIRM_MESSAGES[this.initialClaimTypeStatus];
      if (!confirmMessage) return "";
      return confirmMessage[this.claimTypeStatus] || "";
    },
  },

  methods: {
    handleClickCancel() {
      this.$router.push({ name: "hull-insurance-claim-type-list" });
    },
    async handleClickCreate() {
      if (
        this.claimStatusConfirmMessage &&
        this.claimStatusConfirmMessage.length > 0
      ) {
        this.$refs.ConfirmModal.show();
      } else {
        await this.updateClaimTemplate();
      }
    },
    cancelConfirm() {
      setTimeout(() => {
        this.$refs.ConfirmModal.close();
      });
    },
    acceptConfirm() {
      this.updateClaimTemplate();
      setTimeout(() => {
        this.$refs.ConfirmModal.close();
      });
    },
    async updateClaimTemplate() {
      try {
        this.$vs.loading();
        this.updateClaimTypeState();
        const res = await claimTypeRepository.updateClaimType(
          this.claimType.id,
          this.createClaimTypeRequest()
        );
        if (!res.status) {
          if (!this.isVersionConflicted(res.code)) {
            throw new Error("Errors other than version conflicts");
          }
          this.$vs.notify({
            title: VERSION_CONFLICTED_MESSAGE,
            color: "danger",
            iconPack: "feather",
            icon: "icon-x-square",
          });
        } else {
          this.createSuccess = true;
        }
      } catch (_e) {
        this.$vs.notify({
          title: CLAIM_TEMPLATE_UPDATE_FAIL_MESSAGE,
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
        });
      } finally {
        this.$nextTick(() => {
          this.$vs.loading.close();
        });
      }
    },
    changeClaimTypeStatus(nextStatusValue) {
      this.claimType.status = nextStatusValue;
    },
    createClaimTypeRequest() {
      return {
        status: this.createNextClaimTypeStatus(),
        step_items: this.claimTypeSteps.reduce(
          (acc, { id, claimTypeStepItems }) => {
            acc[id] = claimTypeStepItems.map(({ mandatory, claimItem }) => {
              return {
                mandatory: !!mandatory,
                item_id: claimItem.id,
              };
            });
            return acc;
          },
          {}
        ),
        version: this.claimType.version,
      };
    },
    updateClaimTypeState() {
      Vue.set(
        this.claimType,
        "claimTypeSteps",
        this.getModifiedClaimTypeSteps()
      );
    },
    getModifiedClaimTypeSteps() {
      return this.claimTypeSteps.map(
        claimTypeStep =>
          this.$refs[`claimTypeSteps-${claimTypeStep.id}`][0].claimTypeStep
      );
    },
    createNextClaimTypeStatus() {
      const { claimTypeStatus } = this;
      if (claimTypeStatus === UNREGISTERED) {
        return CREATING;
      }
      return claimTypeStatus;
    },
    checkHasAddedClaimItem() {
      this.hasAddedClaimItem = true;
    },
    isVersionConflicted(
      statusCode // number
    ) {
      return statusCode === 409;
    },
  },

  mounted() {},
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";
@import "@/assets/scss/aisea/_typography.scss";
@import "@/assets/scss/aisea/hull-insurance.scss";
.claim-type-form {
  overflow-x: auto;
  background: $aisea-pure-white;
  border-radius: 8px;

  .claim-type-form-header-area {
    height: 150px;
    border-bottom: #d9d9d9 solid 1px;
    .claim-type-form-header-area-status {
      margin-top: 50px;
    }
  }
  .claim-type-form-success-area {
    .claim-type-form-success-message {
      font-size: 14px;
    }
    .to-claim-type-list-btn {
      width: 240px;
    }
  }

  .claim-type-form-step-area {
    .claim-type-form-step-area-title {
      color: #757575;
      font-family: $aisea-font-family;
      font-size: 20px;
      font-weight: 700;
    }
  }
  .free-text-template-area {
    border-bottom: #d9d9d9 solid 1px;
    .free-annotation-text {
      font-size: 14px;
      font-family: $aisea-font-family;
      color: $common-text-dark-gray;
    }
    .free-text-area,
    .other-attachments {
      gap: 8px;
      .free-text-sub-title {
        font-size: 14px;
      }
      .free-text-btn-area {
        .free-text-template {
          height: 240px;
          max-width: 720px;
        }
      }
    }
  }
  .claim-type-form-submit {
    height: 150px;
    .cancel-btn,
    .create-btn {
      width: 200px;
      height: 50px;
    }
  }
}
</style>
